import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Card, Col, Icon, message, Modal, notification, Row, Typography } from 'antd'
import { get } from 'lodash'

import StarIcon from '../../../assets/images/Vector.svg'
import { RULES, validRule } from '../../../constants/Entitlements'
import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import { retrieveConfiguration, validateCanEditWalletByRange } from '../utils'
import CustomerAvailableList from './CustomerAvailableList'
import Header from './Header'
import List from './List'
import WalletStatusCard from './WalletStatusCard'

import './styles.less'

const CreateCustomerWallet = ({ match, history }) => {
  const districtId = match.params.id

  const compState = {
    cardView: true,
    modalVisible: false,
    customerList: [],
    customerAvailableList: [],
    configuration: { areaEndDate: null, areaStartDate: null },
    memberData: {},
    showRemoveButton: false,
    selectedRows: [],
    selectedRowKeys: [],
    formattedLocationTypes: [],
    filterForm: {
      locationType: ''
    },
    addCustomerSelectedRows: [],
    addCustomerPagination: {},
    addCustomerTableLoading: false,
    walletCustomerTableLoading: true,
    pagination: {},
    addCustomersModalSubTitle: '',
    addCustomerSelectedRowsKeys: []
  }
  const listSelectedRowKeys = useRef([])
  const addCustomersModalRef = useRef()
  const [state, setState] = useState(compState)
  const [walletStatus, setWalletStatus] = useState({})
  const gridRef = useRef()
  const { memberId } = Object.fromEntries(new URLSearchParams(useLocation().search))
  const auth = useSelector((reduxState) => reduxState.auth)

  useEffect(() => {
    if (!compState.addCustomerTableLoading) {
      loadWalletByDistrict(state.updatedState)
    }
  }, [compState.addCustomerTableLoading])

  // Mount/Umount component
  useEffect(() => {
    try {
      const _hasAccess = validRule(RULES.CUSTOMER_LINKS)
      if (!_hasAccess) {
        return history.push('/home')
      }

      onCreateContainer()
    } catch {
      message.error(getMessage('walletNotFound'))
    }
  }, [])

  // Async queue for mount component
  async function onCreateContainer() {
    const districtWallets = await request(api.listEibCarteiraEleitos({ districtId }))
    const memberWallet = districtWallets.find(
      ({ district_id, member_id }) => district_id == districtId && member_id == memberId
    )
    compState.memberData = memberWallet
    const formattedCustomerList = await loadWalletByDistrict()
    const formattedResponse = await listCustomers()

    const formattedConfiguration = await retrieveConfiguration(state.memberData['organization_id'])
    const updatedState = {
      ...state,
      memberData: memberWallet,
      customerList: formattedCustomerList,
      customerAvailableList: formattedResponse,
      configuration: formattedConfiguration,
      walletCustomerTableLoading: false,
      addCustomersModalSubTitle: `${state.memberData.customers_count} ${getMessage('of')} ${
        formattedConfiguration.clientQuantity
      } ${getMessage('listCustomer')}`
    }

    setState(updatedState)
    getLocationTypes(updatedState)
  }

  async function getLocationTypes(currentState) {
    try {
      const locationTypesDropdown = await request(api.listTypes(), true)
      const updatedState = {
        ...currentState,
        formattedLocationTypes: locationTypesDropdown
      }
      setState(updatedState)
      return
    } catch (error) {
      console.error({ error })
    }
  }

  async function listCustomers(search) {
    try {
      const response = await request(api.listEibCarteiraEleitosAvailableLocations(districtId))
      let filteredItems

      if (!search) {
        filteredItems = response.filter((item) => item.customerName !== null)
      } else {
        filteredItems = response.filter(
          (item) => item.customerName && item.customerName.toLowerCase().includes(search.toLowerCase())
        )
      }

      const formattedValues = filteredItems.map((item) => {
        return {
          key: `${get(item, 'customerId')}_${get(item, 'id')}`,
          customerId: get(item, 'customerId'),
          customerName: get(item, 'customerName'),
          locationId: get(item, 'id'),
          location: get(item, 'locationName'),
          areaTotal: get(item, 'areaPlantada'),
          isOwner: get(item, 'isOwner', false)
        }
      })

      return formattedValues
    } catch (error) {
      console.tron.log(error)
      return []
    }
  }

  // Cruds
  async function retrieveWalletStatus() {
    const memberWallet = await request(api.listEibCarteiraEleitos({ districtId }))
    if (memberWallet && memberWallet.length) {
      setWalletStatus({
        id: memberWallet[0].status_id,
        name: memberWallet[0].status_name
      })
    }
  }

  async function loadWalletByDistrict(updatedState = null) {
    try {
      const response = await request(api.regionLinksByDistrict(districtId), updatedState !== null)

      await retrieveWalletStatus()
      let formattedCustomerWallet = []
      if (Array.isArray(response)) {
        formattedCustomerWallet = response.map((item) => ({
          key: item.id,
          harvestId: item.harvestId,
          customerId: item.customerId,
          customer: item.customerName,
          location: item.locationName,
          category: item.locationType,
          areaTotal: item.area_total,
          customerLinkId: item.id,
          focalMemberId: item.focalMemberId,
          focalDistrictId: item.focalDistrictId,
          focalHarvestId: item.focalHarvestId,
          action: item.id
        }))
      }

      return formattedCustomerWallet
    } catch (error) {
      console.tron.log(error)
      return []
    }
  }

  function getTitle() {
    return state.memberData.customers_count > 0
      ? getMessage('updateCustomerWallet')
      : getMessage('createCustomerWallet')
  }

  function addElectToListButtonClick() {
    if (Number(state.memberData.customers_count) === Number(state.configuration.clientQuantity)) {
      notification.warning({
        description: getMessage('maxCustomersWarning'),
        message: getMessage('sweetAlerts.warning')
      })
      return
    }

    const startDate = state.configuration.clientEditionPeriodStartDate
    const endDate = state.configuration.clientEditionPeriodEndDate
    if (!validateCanEditWalletByRange(startDate, endDate, true)) {
      return
    }

    setState({ ...state, modalVisible: !state.modalVisible })
  }

  async function removeLocationFromWallet(regionLinksIds) {
    try {
      await request(api.deleteRegionLink(regionLinksIds[0])).then(async () => {
        const updatedCustomerList = state.customerList.filter(
          (currentCustomer) => !regionLinksIds.includes(currentCustomer.key)
        )
        const formattedResponse = await listCustomers()

        const updatedMemberData = {
          ...state.memberData
        }
        listSelectedRowKeys.current = []
        updatedMemberData.customers_count = parseInt(state.memberData.customers_count) - regionLinksIds.length
        const updatedState = {
          ...state,
          customerAvailableList: formattedResponse,
          customerList: updatedCustomerList,
          memberData: updatedMemberData,
          showRemoveButton: false,
          selectedRows: [],
          selectedRowKeys: [],
          addCustomerTableLoading: false
        }
        setState(updatedState)
        const historyState = {
          ...history.location.state,
          customers_count: updatedMemberData.customers_count
        }
        history.replace({ pathname: history.location.pathname, state: historyState })
      })
      notification.success({
        description: getMessage('deleteCustomersSuccess'),
        message: getMessage('sweetAlerts.success')
      })
    } catch (error) {
      notification.error({
        description: getMessage('deleteCustomersError'),
        message: getMessage('sweetAlerts.error')
      })
      console.tron.log(error)
    }
  }

  function renderCustomersTable() {
    return (
      <>
        {state.customerList.length === 0 ? (
          <div style={{ textAlign: '-webkit-center', margin: '10%' }}>
            <div>
              <img src={StarIcon} alt="" />
              <Typography
                className="default-header-title"
                style={{ color: '#334863', padding: '10px 0px 10px' }}
                level={3}
              >
                {getMessage('walletAddDescription')}
              </Typography>
            </div>
          </div>
        ) : (
          <List
            state={state}
            setState={setState}
            customersList={state.customerList}
            ref={gridRef}
            removeLocationFromWallet={removeLocationFromWallet}
            memberId={memberId}
            districtId={districtId}
          />
        )}
      </>
    )
  }

  function getAddCustomersModalTitle() {
    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Typography.Title level={4}>{getMessage('selectCustomers')}</Typography.Title>
        <Typography.Text className="label-select-customer-count" level={4}>
          {state.addCustomersModalSubTitle}
        </Typography.Text>
      </div>
    )
  }

  function getAddCustomerModel() {
    return (
      <Modal
        title={getAddCustomersModalTitle()}
        visible={state.modalVisible}
        width={1000}
        ref={addCustomersModalRef}
        footer={null}
        onCancel={() => setState({ ...state, modalVisible: !state.modalVisible })}
      >
        <Row gutter={[16, 24]}>
          <Col span={24}>
            <CustomerAvailableList
              state={state}
              setState={setState}
              history={history}
              loadWalletByDistrict={loadWalletByDistrict}
              listCustomers={listCustomers}
            />
          </Col>
        </Row>
      </Modal>
    )
  }

  return (
    <div className="createCustomerWallet">
      <Header
        match={match}
        history={history}
        walletStatusId={walletStatus.id}
        state={state}
        addElectToListButtonClick={addElectToListButtonClick}
        loggedUser={auth.auth}
      />
      <Card className="ant-card gx-card ant-card-bordered">
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Icon type="down" style={{ color: '#263447', fontSize: 17, marginTop: 5, marginRight: 5 }} />
          <Typography.Title className="default-header-title" level={3}>
            {getMessage('walletClientInformation')}
          </Typography.Title>
        </div>
        <WalletStatusCard
          state={state}
          walletStatus={walletStatus}
          profileId={auth.auth.profileId}
          retrieveWalletStatus={retrieveWalletStatus}
        />
        <Typography.Title className="default-header-title" level={3}>
          {getTitle()}
        </Typography.Title>
        {renderCustomersTable()}
      </Card>
      {getAddCustomerModel()}
    </div>
  )
}

export default CreateCustomerWallet
